import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, ArrowRight } from "lucide-react";
import { fetchBusTastings } from "../lib/api";
import { useAuth } from "../lib/AuthProvider";

/**
 * Compact Dashboard callout — only renders when there are ≥1 "hot days"
 * (days with ≥2 requested bus events). Admin + dispatcher only. Clicking
 * the row jumps to the Bus Events tab where those day groups are already
 * marked HOT. Stays silent when nothing's hot so the Dashboard doesn't
 * get cluttered on normal days.
 */
export function HotDayCallout() {
  const { user } = useAuth();
  const [hotDays, setHotDays] = useState(null); // null = loading, [] = quiet

  useEffect(() => {
    if (!user || !["admin", "dispatcher"].includes(user.role)) return;
    let cancelled = false;
    fetchBusTastings()
      .then((all) => {
        if (cancelled) return;
        const byDate = {};
        for (const t of all || []) {
          if (t.status !== "requested" || !t.requested_date) continue;
          (byDate[t.requested_date] = byDate[t.requested_date] || []).push(t);
        }
        const hot = Object.entries(byDate)
          .filter(([, list]) => list.length >= 2)
          .map(([date, list]) => ({ date, count: list.length }))
          .sort((a, b) => a.date.localeCompare(b.date));
        setHotDays(hot);
      })
      .catch(() => !cancelled && setHotDays([]));
    return () => { cancelled = true; };
  }, [user]);

  if (!user || !["admin", "dispatcher"].includes(user.role)) return null;
  if (!hotDays || hotDays.length === 0) return null;

  const totalRequests = hotDays.reduce((n, d) => n + d.count, 0);
  const nextThree = hotDays.slice(0, 3);

  return (
    <Link
      to="/bus-tastings"
      data-testid="hot-day-callout"
      className="group block panel border-amber-500/40 bg-amber-500/8 hover:bg-amber-500/12 hover:border-amber-500/60 transition-colors px-4 py-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="shrink-0 w-8 h-8 rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
            <Flame className="w-4 h-4 text-amber-300" />
          </div>
          <div className="min-w-0">
            <div className="text-sm text-amber-100 font-medium">
              {hotDays.length} hot day{hotDays.length === 1 ? "" : "s"} coming up
              <span className="text-amber-300/80 font-mono text-[11px] ml-2">
                {totalRequests} requests stacked
              </span>
            </div>
            <div className="text-[11px] text-amber-200/70 mt-0.5 truncate font-mono">
              {nextThree.map((d) => `${d.date} (${d.count})`).join(" · ")}
              {hotDays.length > 3 ? ` · +${hotDays.length - 3} more` : ""}
            </div>
          </div>
        </div>
        <div className="text-amber-300 group-hover:text-amber-200 flex items-center gap-1 text-xs font-mono shrink-0">
          Schedule routes
          <ArrowRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
}
